/**
 * User Routes
 * CD-269: Setup Vue Router
 * Routes for the user portal (portal.clonedetector.com)
 */
export default [
  // Authenticated routes with DefaultLayout
  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/user/DashboardView.vue'),
        meta: { requiresAuth: true, title: 'Dashboard', keepAlive: true }
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/user/ProfileView.vue'),
        meta: { requiresAuth: true, title: 'My Profile' }
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@/views/user/BillingView.vue'),
        meta: { requiresAuth: true, title: 'Billing & Credits', keepAlive: true }
      },
      {
        path: 'billing/success',
        name: 'billing-success',
        component: () => import('@/views/user/BillingSuccessView.vue'),
        meta: { requiresAuth: true, title: 'Payment Successful' }
      },
      {
        path: 'billing/cancel',
        name: 'billing-cancel',
        redirect: { name: 'billing' }
      },
      {
        path: 'domains',
        name: 'domains',
        component: () => import('@/views/user/DomainsView.vue'),
        meta: { requiresAuth: true, title: 'My Domains', keepAlive: true }
      },
      {
        path: 'scan/new',
        name: 'new-scan',
        component: () => import('@/views/user/NewScanView.vue'),
        meta: { requiresAuth: true, title: 'New Scan' }
      },
      {
        path: 'scans',
        name: 'scan-history',
        component: () => import('@/views/user/ScanHistoryView.vue'),
        meta: { requiresAuth: true, title: 'Scan History', keepAlive: true }
      },
      {
        // :id is either an integer (CDUMS scan_queue.id) or a 16-char
        // hex CDBE scan_id. Constraining at the route level rejects
        // confused-deputy-style URL tampering before the view fetches.
        // Backend auth still enforces ownership; this is defense-in-depth.
        path: 'scans/:id(\\d+|[0-9a-f]{16})',
        name: 'scan-detail',
        component: () => import('@/views/user/ScanDetailView.vue'),
        meta: { requiresAuth: true, title: 'Scan Details' }
      },
      {
        path: 'api-keys',
        name: 'api-keys',
        component: () => import('@/views/user/ApiKeysView.vue'),
        meta: { requiresAuth: true, title: 'API Keys' }
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/user/SettingsView.vue'),
        meta: { requiresAuth: true, title: 'Settings' }
      }
    ]
  },
  // Auth routes with AuthLayout
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { guestOnly: true, title: 'Sign In' }
      },
      {
        path: 'register',
        name: 'register',
        component: () => import('@/views/auth/RegisterView.vue'),
        meta: { guestOnly: true, title: 'Create Account' }
      },
      {
        path: 'verify-otp',
        name: 'verify-otp',
        component: () => import('@/views/auth/VerifyOtpView.vue'),
        meta: { guestOnly: true, title: 'Verify Email' }
      },
      {
        path: 'set-password',
        name: 'set-password',
        component: () => import('@/views/auth/SetPasswordView.vue'),
        meta: { guestOnly: true, title: 'Set Password' }
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: () => import('@/views/auth/ForgotPasswordView.vue'),
        meta: { guestOnly: true, title: 'Reset Password' }
      },
      {
        path: 'reset-password/:token',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
        meta: { guestOnly: true, title: 'Set New Password' }
      },
      {
        path: 'social/callback',
        name: 'social-callback',
        component: () => import('@/views/auth/SocialCallbackView.vue'),
        meta: { title: 'Signing in...' }
        // No guestOnly — view must run even after token is stored
      }
    ]
  },
  // Onboarding wizard — uses AuthLayout, requires authentication
  {
    path: '/onboarding',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'onboarding',
        component: () => import('@/views/auth/OnboardingView.vue'),
        meta: { requiresAuth: true, title: 'Complete Your Profile' }
      }
    ]
  },
  // CD-386: Direct reset-password route for email links
  // Backend sends links as /reset-password?token=xxx
  // CD-496: Wrapped in AuthLayout for proper centering (was rendering bare with large empty space)
  {
    path: '/reset-password',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: '',
        name: 'reset-password-direct',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
        meta: { guestOnly: true, title: 'Set New Password' }
      }
    ]
  },
  // Error pages
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: { title: 'Access Denied' }
  },
  // 404 catch-all
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page Not Found' }
  }
]
